import { useEffect, useState } from 'react';
import { Form } from 'antd';
import { MLLocationOption, PublishToMLController, PublishToMLFragmentProps } from './interfaces';
import { ML_PORTAL_ID } from 'services/sales/sale.dto';
import { useMessenger } from 'tools/view-hooks/messenger-hook';

export const usePublishToMLController = (props: PublishToMLFragmentProps): PublishToMLController => {
    const { salesService, propertyId, onPublished } = props;
    const messenger = useMessenger();
    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingStates, setIsLoadingStates] = useState(false);
    const [isLoadingCities, setIsLoadingCities] = useState(false);
    const [isLoadingNeighborhoods, setIsLoadingNeighborhoods] = useState(false);
    const [stateOptions, setStateOptions] = useState<MLLocationOption[]>([]);
    const [cityOptions, setCityOptions] = useState<MLLocationOption[]>([]);
    const [neighborhoodOptions, setNeighborhoodOptions] = useState<MLLocationOption[]>([]);

    useEffect(() => {
        setIsLoadingStates(true);
        salesService
            .getMLStates()
            .then((results) => setStateOptions(results.map((r) => ({ value: r.id, label: r.name }))))
            .catch(() => messenger.showErrorMessage({ key: 'Error al cargar provincias' }))
            .finally(() => setIsLoadingStates(false));
    }, []);

    const onStateChange = (stateId: string) => {
        form.setFieldsValue({ mlCityId: undefined, mlNeighborhoodId: undefined });
        setCityOptions([]);
        setNeighborhoodOptions([]);
        setIsLoadingCities(true);
        salesService
            .getMLCities(stateId)
            .then((results) => setCityOptions(results.map((r) => ({ value: r.id, label: r.name }))))
            .catch(() => messenger.showErrorMessage({ key: 'Error al cargar ciudades' }))
            .finally(() => setIsLoadingCities(false));
    };

    const onCityChange = (cityId: string) => {
        form.setFieldsValue({ mlNeighborhoodId: undefined });
        setNeighborhoodOptions([]);
        setIsLoadingNeighborhoods(true);
        salesService
            .getMLNeighborhoods(cityId)
            .then((results) => setNeighborhoodOptions(results.map((r) => ({ value: r.id, label: r.name }))))
            .catch(() => messenger.showErrorMessage({ key: 'Error al cargar barrios' }))
            .finally(() => setIsLoadingNeighborhoods(false));
    };

    const onFormSubmit = (values: {
        mlCategoryId: string;
        listingTypeId: string;
        mlStateId: string;
        mlCityId: string;
        mlNeighborhoodId: string;
        rooms?: number;
        bathRooms?: number;
        parkingLots?: number;
    }) => {
        setIsLoading(true);
        salesService
            .publishToML(propertyId, {
                portalId: ML_PORTAL_ID,
                listingTypeId: values.listingTypeId,
                mlCategoryId: values.mlCategoryId,
                mlNeighborhoodId: values.mlNeighborhoodId,
                rooms: values.rooms,
                bathRooms: values.bathRooms,
                parkingLots: values.parkingLots,
            })
            .then((listing) => {
                form.resetFields();
                setStateOptions([]);
                setCityOptions([]);
                setNeighborhoodOptions([]);
                onPublished(listing);
            })
            .catch(() => messenger.showErrorMessage({ key: 'Error al publicar en MercadoLibre' }))
            .finally(() => setIsLoading(false));
    };

    return {
        isLoading,
        isLoadingStates,
        isLoadingCities,
        isLoadingNeighborhoods,
        stateOptions,
        cityOptions,
        neighborhoodOptions,
        form,
        onStateChange,
        onCityChange,
        onFormSubmit,
    };
};
